<template>
  <div class="account_close" :class="{ 'ac-page': viewMode !== 'notice' }">
    <template v-if="viewMode === 'success'">
      <div class="ac-body center-top">
        <div class="success-mark">
          <svg width="34" height="34" viewBox="0 0 34 34" fill="none">
            <path
              d="M7 17.5l6.5 6.5L27 10"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <h1 class="success-title">注销申请提交成功</h1>
        <p class="success-sub">你的注销申请正在处理中</p>

        <div class="success-card">
          <p>
            亲爱的用户，你的账号 <b>{{ successAccountName }}</b> 于 {{ closeResult.applyTime }} 申请注销，已完成条件审核。除法律法规规定和注销协议约定情形外，我们将在你提交申请之日起的 {{ auditDays }} 日内完成注销。
          </p>
          <p class="warn">
            在上述 {{ auditDays }} 日届满前，即 {{ closeResult.expireTime }} 前，请你不要登录账号，以便注销顺利完成，否则视为你撤销账号注销申请。
          </p>
        </div>
      </div>

      <div class="ac-footer">
        <button class="ac-btn is-primary" @click="onKnow">我知道了</button>
      </div>
    </template>

    <template v-else-if="viewMode === 'realName'">
      <div class="ac-body">
        <h1 class="ac-title">验证实名信息</h1>
        <p class="form-tip">当前账号未绑定手机号，请填写账号实名信息后继续注销。</p>

        <div class="form">
          <div class="ac-row">
            <label class="ac-label"><i class="req">*</i>姓名</label>
            <div class="ac-field">
              <input
                v-model.trim="realNameForm.idCardName"
                class="ac-input"
                placeholder="请输入账号绑定的姓名"
              />
            </div>
          </div>

          <div class="ac-row">
            <label class="ac-label"><i class="req">*</i>身份证</label>
            <div class="ac-field">
              <input
                v-model.trim="realNameForm.idCardNum"
                maxlength="18"
                class="ac-input"
                placeholder="请输入账号绑定的身份证"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="ac-footer">
        <button
          class="ac-btn is-primary"
          :disabled="submitLoading"
          @click="handleRealNameSubmit"
        >
          {{ submitLoading ? "提交中..." : "确认注销" }}
        </button>
        <button class="ac-btn is-text" @click="backToNotice">返回上一步</button>
      </div>
    </template>

    <template v-else>
      <div class="content">
        <section class="card account_card">
          <p class="account_label">当前申请注销账号：</p>
          <p class="account_name">{{ accountName || '--' }}</p>
        </section>

        <section class="card notice_card">
          <p class="warning">
            请注意：您正在申请注销您的账号，请谨慎操作，
            <span class="text_red">一旦点击“提交”或“确认”，即表示您同意注销且放弃账号的权益。账号为通用账号，一旦被注销，该账号关联的所有游戏账号都将注销，无法恢复。</span>
            在您提交注销账申请前，请确认以下内容。
          </p>

          <div v-for="item in noticeList" :key="item.title" class="notice_item">
            <h3>
              {{ item.title }}
              <span v-if="item.redTitle" class="text_red">{{ item.redTitle }}</span>
            </h3>
            <p v-for="text in item.desc" :key="text">{{ text }}</p>
          </div>
        </section>

        <div class="agreement">
          <Checkbox v-model="checked" shape="square" checked-color="#f53648" />
          <div class="agreement_text">
            我自愿放弃账号内所有财产或权益，且已阅读并同意
            <span class="link" @click="goNotice">《账号注销须知》</span>。
          </div>
        </div>
      </div>

      <div class="footer">
        <Button
          block
          round
          type="danger"
          :loading="submitLoading"
          loading-text="提交中..."
          @click="handleSubmit"
        >
          提交
        </Button>
      </div>

      <div v-if="smsVisible" class="sms_mask">
        <div class="sms_dialog">
          <h3>短信验证</h3>
          <p>验证码将发送至绑定手机号 {{ mobile }}，请完成验证后继续注销。</p>
          <Field
            v-model.trim="smsCode"
            type="digit"
            maxlength="6"
            placeholder="请输入验证码"
            clearable
            class="sms_field"
          />
          <div class="sms_actions">
            <Button plain round size="small" @click="closeSmsDialog">取消</Button>
            <Button
              plain
              round
              size="small"
              :disabled="countdown > 0"
              @click="sendSmsCode"
            >
              {{ countdown > 0 ? `${countdown}s后重发` : '重新发送' }}
            </Button>
            <Button
              round
              size="small"
              type="danger"
              :loading="submitLoading"
              @click="confirmCloseWithSms"
            >
              确认
            </Button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Button, Checkbox, Field, Toast, Dialog } from "vant";
import {
  setPageTitle,
  toLogin,
  onClosePage,
  notifyAccountClosed,
} from "@/bridge/AppJsBridge";
import {
  getWxUserStatusV2Api,
  sendWxUserCloseSmsApi,
  getWxUserRealNameStatusApi,
  closeWxUserApi,
} from "@/apis/wxActivity";
import EventTrack from "@/mixins/eventTrack";
import { reg, COOLING_DAYS } from "@/views/gameJump/accountCancel/config";

export default {
  name: "AccountClose",
  mixins: [EventTrack],
  components: {
    Button,
    Checkbox,
    Field,
  },
  data() {
    return {
      viewMode: "notice",
      checked: false,
      submitLoading: false,
      userStatus: {},
      userInfo: {},
      smsVisible: false,
      smsCode: "",
      countdown: 0,
      timer: null,
      skipInit: false,
      realNameForm: {
        idCardName: "",
        idCardNum: "",
      },
      closeResult: {},
      noticeList: [
        {
          title: "1.账号处于安全状态",
          desc: [
            "您的账号没有存在被盗、被封禁等风险。您的账号不存在其他未结纠纷，包括投诉举报、被投诉举报、仲裁、诉讼等纠纷。",
          ],
        },
        {
          title: "2.注销后账号的资产和权益将被",
          redTitle: "清空",
          desc: [
            "包括但不限于该账号获得的充值余额、游戏币、道具、优惠券、积分、已购云挂机服务（月卡、体验卡、季卡、年卡等）、虚拟币、虚拟物品、转游点、代金券、礼包以及未来可能产生的收益，请妥善处理，若未处理，该账号中的资产及权益将视为自愿放弃且被删除，您将无法继续使用。",
          ],
        },
        {
          title: "3.注销后账号及产品数据将",
          redTitle: "被删除且无法找回",
          desc: [
            "（1）您的账号基本信息、实名信息、身份证等信息将被清空且无法恢复；",
            "（2）账号注销后，该账号下所有信息、数据、记录、发布的内容（如贴子、点赞、收藏、关注、评论、回复）将会被删除或者匿名化处理，且无法找回；",
            "（3）账号注销后，将无法登录，且该账号绑定的手机、微信、QQ、微博、抖音等将自动解除绑定；",
            "（4）账号注销后，该账号及其关联的其他游戏账号及角色将一并注销。",
          ],
        },
      ],
    };
  },
  computed: {
    accountName() {
      return this.userStatus.userName || this.userInfo.username || "";
    },
    mobile() {
      return this.userStatus.telNum || "";
    },
    hasMobile() {
      return this.userStatus.telStatus === 1;
    },
    uid() {
      // return '580462128'
      return Number(this.cookie && (this.cookie.uid || this.cookie.userId)) || undefined;
    },
    appkey() {
      const appkey =
        this.$route.query.appkey ||
        this.$route.query.gameId ||
        (this.cookie && this.cookie.appkey) ||
        "200010949";
      return String(appkey);
    },
    auditDays() {
      return this.closeResult.auditDays || COOLING_DAYS;
    },
    successAccountName() {
      return this.accountName || "当前账号";
    },
  },
  created() {
    setPageTitle("注销账号");
    this.getAppToken(true);
  },
  beforeDestroy() {
    this.clearTimer();
  },
  methods: {
    getActivityInfo () {
      this.getUserStatus();
    },
    getUserStatus() {
      const params = {};
      if (this.uid) {
        params.uid = this.uid;
      }
      getWxUserStatusV2Api(params)
        .then((res) => {
          this.userStatus = res.data || {};
        })
        .catch((err) => {
          if (err && err.code === 401) {
            toLogin("/loginKey/LoginByKeyPhoneActivity");
          }
        });
    },
    goNotice() {
      this.$router.push({ name: "box.accountCloseNotice" });
    },
    handleSubmit() {
      if (this.submitLoading) {
        return;
      }
      if (!this.checked) {
        Toast("请先阅读并同意《账号注销须知》");
        return;
      }

      if (this.hasMobile) {
        this.smsVisible = true;
        this.sendSmsCode();
        return;
      }

      this.queryRealNameAndContinue();
    },
    queryRealNameAndContinue() {
      this.submitLoading = true;
      getWxUserRealNameStatusApi()
        .then((res) => {
          if (res.data === true) {
            this.viewMode = "realName";
            setPageTitle("身份验证");
            return;
          }
          this.confirmCloseWithoutMobile();
        })
        .catch((err) => {
          Toast.fail((err && (err.msg || err.message)) || "实名状态查询失败");
        })
        .finally(() => {
          this.submitLoading = false;
        });
    },
    sendSmsCode() {
      if (this.countdown > 0) return;
      if (!this.appkey) {
        Toast("缺少游戏标识 appkey");
        return;
      }
      sendWxUserCloseSmsApi({ appkey: this.appkey })
        .then(() => {
          Toast("验证码已发送");
          this.startCountdown();
        })
        .catch((err) => {
          Toast.fail((err && (err.msg || err.message)) || "验证码发送失败");
        });
    },
    startCountdown() {
      this.clearTimer();
      this.countdown = 60;
      this.timer = setInterval(() => {
        if (this.countdown <= 1) {
          this.clearTimer();
          this.countdown = 0;
          return;
        }
        this.countdown -= 1;
      }, 1000);
    },
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    closeSmsDialog() {
      this.smsVisible = false;
      this.smsCode = "";
    },
    confirmCloseWithSms() {
      if (!this.smsCode) {
        Toast("请输入短信验证码");
        return;
      }
      this.closeAccount({
        code: this.smsCode,
      });
    },
    validateRealNameForm() {
      if (!this.realNameForm.idCardName) {
        Toast("请输入姓名");
        return false;
      }
      if (!reg.idCard.test(this.realNameForm.idCardNum)) {
        Toast("请输入正确的身份证号");
        return false;
      }
      return true;
    },
    handleRealNameSubmit() {
      if (this.submitLoading || !this.validateRealNameForm()) {
        return;
      }
      Dialog.confirm({
        title: "确认注销账号",
        message: "注销后账号及关联游戏账号将无法恢复，是否继续？",
        confirmButtonText: "继续",
      }).then(() => {
        this.closeAccount({
          idCardName: this.realNameForm.idCardName,
          idCardNum: this.realNameForm.idCardNum,
        });
      }).catch(() => {});
    },
    backToNotice() {
      this.viewMode = "notice";
      setPageTitle("注销账号");
    },
    onKnow() {
      onClosePage();
    },
    confirmCloseWithoutMobile() {
      Dialog.confirm({
        title: "确认注销账号",
        message: "注销后账号及关联游戏账号将无法恢复，是否继续？",
        confirmButtonText: "继续",
      }).then(() => {
        this.closeAccount();
      }).catch(() => {});
    },
    closeAccount(params = {}) {
      if (!this.appkey) {
        Toast("缺少游戏标识 appkey");
        return;
      }
      this.submitLoading = true;
      closeWxUserApi({
        appkey: this.appkey,
        ...params,
      })
        .then((res) => {
          this.closeResult = res.data || {};
          notifyAccountClosed({
            appkey: this.appkey,
            uid: this.uid,
          });
          this.smsVisible = false;
          this.smsCode = "";
          this.clearTimer();
          this.viewMode = "success";
          setPageTitle("注销申请提交成功");
        })
        .catch((err) => {
          Toast.fail((err && (err.msg || err.message)) || "注销申请提交失败");
        })
        .finally(() => {
          this.submitLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../gameJump/accountCancel/styles.scss";

.account_close {
  min-height: 100vh;
  background: #f7f8fa;
  color: #333;
  padding-bottom: 88px;
}

.account_close.ac-page {
  padding-bottom: 0;
  background: $ac-bg;
  color: $ac-text;
}

.content {
  padding: 12px;
}

.card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
}

.account_card {
  display: flex;
  align-items: center;
  font-size: 15px;
  line-height: 22px;
}

.account_label {
  color: #666;
}

.account_name {
  flex: 1;
  color: #111;
  font-weight: 600;
  word-break: break-all;
}

.warning {
  color: #333;
  font-size: 14px;
  line-height: 24px;
}

.text_red {
  color: #f53648;
}

.notice_item {
  margin-top: 18px;

  h3 {
    margin-bottom: 8px;
    color: #111;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
  }

  p {
    color: #555;
    font-size: 14px;
    line-height: 24px;
    text-align: justify;
  }
}

.agreement {
  display: flex;
  align-items: flex-start;
  padding: 4px 4px 16px;
  color: #666;
  font-size: 13px;
  line-height: 20px;
}

.agreement_text {
  flex: 1;
  margin-left: 8px;
}

.link {
  color: #1989fa;
}

.footer {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  padding: 10px 16px;
  background: #fff;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.05);
}

.sms_mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
}

.sms_dialog {
  width: 310px;
  padding: 22px 18px 18px;
  background: #fff;
  border-radius: 14px;

  h3 {
    margin-bottom: 10px;
    color: #111;
    font-size: 18px;
    font-weight: 600;
    text-align: center;
  }

  p {
    color: #666;
    font-size: 14px;
    line-height: 22px;
  }
}

.sms_field {
  margin-top: 16px;
  padding: 0;
  border-bottom: 1px solid #eee;
}

.sms_actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 18px;

  .van-button {
    margin-left: 8px;
  }
}

.form {
  margin-top: 8px;
}

.form-tip {
  margin: 0 0 22px;
  color: $ac-sub;
  font-size: 14px;
  line-height: 1.6;
}

.ac-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: $ac-text;
  font-size: 16px;

  &::placeholder {
    color: rgba(27, 31, 39, 0.42);
  }
}

.center-top {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 36px;
  text-align: center;
}

.success-mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: $ac-brand-grad;
  color: #fff;
}

.success-title {
  margin-top: 20px;
  font-size: 22px;
  font-weight: 700;
}

.success-sub {
  margin-top: 8px;
  color: $ac-sub;
  font-size: 14px;
}

.success-card {
  margin-top: 28px;
  padding: 20px 18px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(20, 25, 35, 0.05);
  text-align: left;

  p {
    color: #3a3f4a;
    font-size: 14px;
    line-height: 1.8;

    b {
      color: $ac-text;
    }
  }

  .warn {
    margin-top: 14px;
    color: $ac-brand;
  }
}

::v-deep .van-checkbox {
  align-items: flex-start;
}

::v-deep .van-checkbox__icon {
  margin-top: 2px;
}
</style>
